/** Screen 3: choose the learning goal, mode, direction and round length. */
import { el, page } from '../dom';
import { navigate } from '../router';
import { getSet } from '../../core/storage';
import { cardsProgress } from '../../core/leitner';
import { cardsOfGroup, hasGroups, listGroups } from '../../core/groups';
import type { Direction, QuizMode } from '../../core/types';
import { startRound } from './quiz';

const MODES: Array<{ value: QuizMode; label: string; hint: string }> = [
  { value: 'choice', label: 'Auswahl', hint: 'Aus 4 Antworten die richtige antippen' },
  { value: 'typing', label: 'Tippen', hint: 'Das Wort selber schreiben' },
  { value: 'flashcard', label: 'Karteikarte', hint: 'Umdrehen und selber bewerten' },
];

const LENGTHS = [10, 20, 30];

const SETTINGS_KEY = 'voci.lastSettings.v1';

/** The learning goal is not remembered: it belongs to one set, not to the app. */
type StoredSettings = { mode: QuizMode; direction: Direction; length: number };

function loadSettings(): StoredSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) return JSON.parse(raw) as StoredSettings;
  } catch {
    // Fall through to the defaults.
  }
  return { mode: 'choice', direction: 'native2foreign', length: 10 };
}

function saveSettings(settings: StoredSettings): void {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

export function learnScreen(context: { params: string[] }): HTMLElement {
  const set = getSet(context.params[0]);

  if (!set) {
    return page({
      title: 'Set nicht gefunden',
      back: '/sets',
      children: [el('div', { class: 'card' }, [el('p', { text: 'Dieses Set gibt es nicht mehr.' })])],
    });
  }

  const settings = loadSettings();

  const directions: Array<{ value: Direction; label: string }> = [
    { value: 'native2foreign', label: `${set.nativeLang} → ${set.foreignLang}` },
    { value: 'foreign2native', label: `${set.foreignLang} → ${set.nativeLang}` },
    { value: 'mixed', label: 'Gemischt' },
  ];

  function optionGroup<T extends string | number>(
    legend: string,
    options: Array<{ value: T; label: string; hint?: string }>,
    selected: T,
    onSelect: (value: T) => void,
  ): HTMLElement {
    const buttons = options.map((option) =>
      el(
        'button',
        {
          class: `chip${option.value === selected ? ' chip-active' : ''}`,
          type: 'button',
          'aria-pressed': option.value === selected,
          onclick: (event: Event) => {
            const group = (event.currentTarget as HTMLElement).parentElement;
            group?.querySelectorAll('.chip').forEach((chip) => {
              chip.classList.remove('chip-active');
              chip.setAttribute('aria-pressed', 'false');
            });
            const target = event.currentTarget as HTMLElement;
            target.classList.add('chip-active');
            target.setAttribute('aria-pressed', 'true');
            onSelect(option.value);
          },
        },
        [
          el('span', { class: 'chip-label', text: option.label }),
          option.hint ? el('span', { class: 'chip-hint', text: option.hint }) : null,
        ],
      ),
    );

    return el('fieldset', { class: 'field' }, [
      el('legend', { class: 'label', text: legend }),
      el('div', { class: 'chips' }, buttons),
    ]);
  }

  const maxLength = Math.max(set.cards.length, 5);
  const lengths = LENGTHS.filter((value) => value <= maxLength);
  if (lengths.length === 0) lengths.push(maxLength);
  if (!lengths.includes(settings.length)) settings.length = lengths[0];

  // Sentinel for "the whole set": an empty string is a valid group name, namely
  // the cards written before the first "==" line.
  const WHOLE_SET = '\u0000all';
  let selectedGroup: string | null = null;

  const meta = el('p', { class: 'set-meta' });
  const activeSet = set;

  function updateMeta(): void {
    const cards = cardsOfGroup(activeSet, selectedGroup);
    const percent = Math.round(cardsProgress(cards) * 100);
    const scope = selectedGroup === null ? 'Ganzes Set' : selectedGroup || 'Ohne Lernziel';
    meta.textContent = `${scope} · ${cards.length} Wörter · ${percent}% gelernt`;
  }

  updateMeta();

  const groupChips = hasGroups(set)
    ? optionGroup(
        'Was möchtest du lernen?',
        [
          { value: WHOLE_SET, label: 'Ganzes Set', hint: `${set.cards.length} Wörter` },
          ...listGroups(set).map((group) => ({
            value: group.name,
            label: group.name || 'Ohne Lernziel',
            hint: `${group.cards.length} Wörter`,
          })),
        ],
        WHOLE_SET,
        (value) => {
          selectedGroup = value === WHOLE_SET ? null : value;
          updateMeta();
        },
      )
    : null;

  return page({
    title: set.name,
    back: '/sets',
    children: [
      el('div', { class: 'card' }, [meta]),
      groupChips ? el('div', { class: 'card' }, [groupChips]) : null,
      el('div', { class: 'card' }, [
        optionGroup('Wie möchtest du üben?', MODES, settings.mode, (value) => {
          settings.mode = value;
        }),
        optionGroup('Richtung', directions, settings.direction, (value) => {
          settings.direction = value;
        }),
        optionGroup(
          'Wie viele Wörter?',
          lengths.map((value) => ({ value, label: String(value) })),
          settings.length,
          (value) => {
            settings.length = value;
          },
        ),
      ]),
      el('div', { class: 'bottom-actions' }, [
        el('button', {
          class: 'btn btn-primary btn-big',
          type: 'button',
          text: 'Los geht\u2019s!',
          onclick: () => {
            saveSettings(settings);
            startRound(set.id, { ...settings, group: selectedGroup }, null);
          },
        }),
        el('button', {
          class: 'btn btn-ghost',
          type: 'button',
          text: 'Statistik ansehen',
          onclick: () => navigate(`/stats/${set.id}`),
        }),
      ]),
    ],
  });
}
